import { Transition } from "@headlessui/react";
import { t } from "i18next";
import { useState, useCallback, SyntheticEvent, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";

import { BannerSubTitle, BannerTitle } from "../styles";

import {
  CarouselCard,
  DotContainer,
  Dot,
  PartnerLogo,
  DescriptionContainer,
  ImageDisplay,
  Title,
  Description,
  ActionButton,
  CarouselDefaultContainer,
  InfoContainerCarousel,
  CarouselGlobals,
  Slide,
  CarouselSlides,
  CarouselContainer,
  BackgroundGradient,
} from "./styles";

import { configState } from "state/config-slice";
import { getPartnerImageUrl } from "utils/getImageUrl";
import Forward from "components/IconImage/forward";
import { offersWithBannerSlides } from "state/offers-slice";
import breakpoints from "themes/default/breakpoints";
import { AppDispatch } from "state/reducers-combiner";
import { sendULFEvent } from "state/config-slice";
import { ULFEvents } from "types/ULF";

const TIME_BETWEEN_SLIDES = 5;

function Carousel() {
  const { cdnUrl } = useSelector(configState);
  const slides = useSelector(offersWithBannerSlides);
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const [counter, setCounter] = useState(0);
  const [paused, setPaused] = useState(true);
  const [videoDuration, setVideoDuration] = useState(TIME_BETWEEN_SLIDES);
  const genericCarouselUrl = useMemo(() => getPartnerImageUrl("generic/carousel", cdnUrl), [cdnUrl]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMediaLoadingRef = useRef(true);
  const slidesRef = useRef<HTMLDivElement>(null);
  const [cookies] = useCookies();
  const [firstSlideFailed, setFirstSlideFailed] = useState(false);

  const updateVideoDuration = useCallback((event: SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = event.target as HTMLVideoElement;
    setVideoDuration(video.duration);
  }, []);

  const pause = useCallback(async () => {
    setPaused(true);

    try {
      await videoRef.current?.pause();
    } catch (e) {
      //
    }
  }, []);

  const resume = useCallback(async () => {
    if (isMediaLoadingRef.current) return;

    setPaused(false);

    try {
      await videoRef.current?.play();
    } catch (e) {
      //
    }
  }, []);

  const disableUserPause = useCallback(() => {
    isMediaLoadingRef.current = true;
    pause();
  }, [pause]);

  const enableUserPause = useCallback(() => {
    isMediaLoadingRef.current = false;
    resume();
  }, [resume]);

  const onMediaLoad = useCallback(() => {
    slidesRef.current?.classList.add("border-radius-bleed-fix");
    enableUserPause();
  }, [enableUserPause]);

  const disableContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    event.preventDefault();
    return false;
  }, []);

  const goToNextSlide = useCallback(() => {
    setCounter((counter) => (counter + 1) % slides.length);

    slidesRef.current?.classList.remove("border-radius-bleed-fix");

    disableUserPause();
  }, [slides.length, disableUserPause]);

  const goToSlideCb = useCallback(
    (slideIndex: number) => () => {
      setCounter(slideIndex % slides.length);

      slidesRef.current?.classList.remove("border-radius-bleed-fix");

      disableUserPause();
    },
    [slides.length, disableUserPause],
  );

  const onFirstSlideFailed = useCallback(() => {
    setFirstSlideFailed(true);
  }, []);

  if (firstSlideFailed && counter === 0 && slides.length > 1) {
    setFirstSlideFailed(false);
    goToNextSlide();
  }

  return (
    <CarouselContainer>
      <CarouselGlobals />

      <CarouselSlides
        id="carousel-base-displayer"
        ref={slidesRef}
        onContextMenu={disableContextMenu}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resume}
        data-testid={"carousel-base-displayer"}
      >
        {slides.map((e, i) => (
          <Transition
            key={e.name}
            as={Slide}
            show={counter === i}
            enter="enter"
            enterFrom="enterFrom"
            enterTo="enterTo"
            leave="leave"
            leaveFrom="leaveFrom"
            leaveTo="leaveTo"
          >
            {i === 0 ? (
              <CarouselDefaultContainer data-testid={"carousel-default-container"}>
                <picture data-testid={"carousel-default-image"}>
                  <source
                    media={`(min-width: ${breakpoints.values.lg}px)`}
                    srcSet={`${genericCarouselUrl}-desktop.webp`}
                  />
                  <source
                    media={`(min-width: ${breakpoints.values.md}px)`}
                    srcSet={`${genericCarouselUrl}-tablet.webp`}
                  />
                  <source srcSet={`${genericCarouselUrl}-mobile.webp`} />
                  <img
                    src={`${genericCarouselUrl}-desktop.webp`}
                    alt=""
                    onLoad={onMediaLoad}
                    onError={onFirstSlideFailed}
                  />
                </picture>

                <InfoContainerCarousel>
                  <BannerSubTitle data-testid={"carousel-default-sub-title"}>
                    {t("Vodafone Subscription+")}
                  </BannerSubTitle>
                  <BannerTitle data-testid={"carousel-default-title"}>
                    {t("Everything you love, in one place.")}
                  </BannerTitle>
                </InfoContainerCarousel>
              </CarouselDefaultContainer>
            ) : (
              <CarouselCard data-testid={`carousel-${e.partner}-video-displayer`}>
                {e.isVideo ? (
                  <ImageDisplay>
                    <video
                      data-testid={`carousel-${e.partner}-video`}
                      ref={counter === i ? videoRef : null}
                      autoPlay
                      muted
                      playsInline
                      controls={false}
                      onLoadedMetadata={updateVideoDuration}
                      onCanPlay={onMediaLoad}
                      onLoadStart={disableUserPause}
                      onWaiting={() => {
                        if (counter === e.order) {
                          disableUserPause();
                        }
                      }}
                      onError={goToNextSlide}
                    >
                      <source src={`${e.images?.carousel.baseUrl}desktop.mp4`} type="video/mp4" />
                    </video>
                  </ImageDisplay>
                ) : (
                  <ImageDisplay>
                    <picture data-testid={`carousel-${e.partner}-image`}>
                      <source
                        media={`(min-width: ${breakpoints.values.lg}px)`}
                        srcSet={`${e.images?.carousel.baseUrl}desktop.webp`}
                      />
                      <source
                        media={`(min-width: ${breakpoints.values.md}px)`}
                        srcSet={`${e.images?.carousel.baseUrl}tablet.webp`}
                      />
                      <source srcSet={`${e.images?.carousel.baseUrl}mobile.webp`} />
                      <img
                        onLoad={onMediaLoad}
                        onError={goToNextSlide}
                        src={`${e.images?.carousel.baseUrl}desktop.webp`}
                        alt=""
                      />
                    </picture>
                  </ImageDisplay>
                )}
                <BackgroundGradient />
                <PartnerLogo background={e.images?.logo} data-testid={`carousel-${e.partner}-logo`} />
                <DescriptionContainer>
                  <Title data-testid={`carousel-${e.partner}-title`}>{e.title}</Title>
                  <Description data-testid={`carousel-${e.partner}-description`}>{e.description}</Description>
                  <ActionButton
                    data-testid={`go-to-${e.partner}-from-carousel`}
                    title={e.action?.label}
                    icon={<Forward />}
                    iconPosition="right"
                    onClick={() => {
                      dispatch(
                        sendULFEvent({
                          page: window.location.pathname,
                          "event-name": ULFEvents.CAROUSEL_CLICKED,
                          "offer-name": e.name,
                          source: cookies.SOURCE,
                          isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
                        }),
                      );

                      navigate(`/offer/${e.name}`);
                    }}
                  />
                </DescriptionContainer>
              </CarouselCard>
            )}
          </Transition>
        ))}
      </CarouselSlides>

      {slides.length > 1 && (
        <DotContainer data-testid={"do-container"}>
          {slides.map((e, i) => (
            <Dot
              onAnimationEnd={goToNextSlide}
              paused={paused}
              timer={e.isVideo ? videoDuration : TIME_BETWEEN_SLIDES}
              isActive={i === counter}
              data-testid={`move-to-${e.partner}`}
              key={e.partner}
              onClick={goToSlideCb(i)}
            />
          ))}
        </DotContainer>
      )}
    </CarouselContainer>
  );
}

export default Carousel;
